import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { debug, log } from '@/services/Log'
import { assertBCCart, type BCCartType } from '@/types/BigCommerce/BCCart'
import type { StandardCart } from '@/types/ShopFront/CheckoutStandards'
import { isShippingProtection } from '@/helpers/isShippingProtection'
import { customerOptedOutOfWFD } from '@/services/FramedCheckout/cartServices/worryFreeDeliverySession'
import { isFeatureEnabled } from '@/helpers/isFeatureEnabled'
import { withWorryFreeQuote } from './withWorryFreeQuote'
import { rawAddWarranty } from './rawAddWarranty'

const formCartStandardizer = ({
  generateLineItemsFromBCCart,
  getCustomItems,
  standardizeCart,
}: {
  generateLineItemsFromBCCart: Awaited<typeof import('@/services/FramedCheckout/orderServices/generateLineItemsFromBCCart')>['generateLineItemsFromBCCart'],
  getCustomItems: Awaited<typeof import('@/helpers/checkoutHelpers/index')>['getCustomItems'],
  standardizeCart: Awaited<typeof import('@/services/Standardizers/standardizeCart')>['standardizeCart'],
}) => async (bcCart: BCCartType) => {
  const lineItems = await generateLineItemsFromBCCart(bcCart)
  const customItems = getCustomItems({ lineItems, customItems: bcCart?.lineItems?.customItems })
  const standardizedCart = standardizeCart({
    ...bcCart,
    email: bcCart.email || '',
    lineItems: [...lineItems, ...customItems],
  })
  return standardizedCart
}

const removeWFD = async (cart: StandardCart) => {
  const [{ rawCartDeleteItem }] = await allPromisesWithRetries(() => [import('@/services/Cart/rawCartDeleteItem')])
  const shippingProtections = cart.lineItems.filter(isShippingProtection)
  for (let i = 0; i < shippingProtections.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await rawCartDeleteItem({
      cartId: cart.id,
      itemId: shippingProtections[i].id,
    })
  }
}

export const asyncStandardizeCart = async (bcCart: BCCartType): Promise<StandardCart> => {
  const [
    { standardizeCart },
    { generateLineItemsFromBCCart },
    { getCustomItems },
  ] = await allPromisesWithRetries(() => [
    import('@/services/Standardizers/standardizeCart'),
    import('@/services/FramedCheckout/orderServices/generateLineItemsFromBCCart'),
    import('@/helpers/checkoutHelpers/index'),
  ])
  const standardizer = formCartStandardizer({
    generateLineItemsFromBCCart,
    getCustomItems,
    standardizeCart,
  })
  return standardizer(bcCart)
}

export const getCart = async (
  providedRawCart?: BCCartType,
): Promise<StandardCart | null> => {
  debug('getCart START')
  // Returns a Cart. - https://developer.bigcommerce.com/api-reference/storefront/carts/cart/getacart
  const [
    { standardizeCart },
    { generateLineItemsFromBCCart },
    { getCustomItems },
    { rawGetCart },
  ] = await allPromisesWithRetries(() => [
    import('@/services/Standardizers/standardizeCart'),
    import('@/services/FramedCheckout/orderServices/generateLineItemsFromBCCart'),
    import('@/helpers/checkoutHelpers/index'),
    import('@/services/Cart/rawGetCart'),
  ])
  let validProvidedRawCart: BCCartType | null = null
  try {
    if (providedRawCart) {
      validProvidedRawCart = assertBCCart(providedRawCart)
    }
  } catch (e) {
    log('Invalid providedRawCart', { providedRawCart, error: e })
  }
  const rawCart: BCCartType = validProvidedRawCart || await rawGetCart()
  if (!rawCart) {
    return null
  }

  // Adds isFurniture boolean property and availability message to lineItems
  const initialCart = await formCartStandardizer({
    generateLineItemsFromBCCart,
    getCustomItems,
    standardizeCart,
  })(rawCart)
  log('initialCart', initialCart)
  const shippingProtections = initialCart.lineItems.filter(isShippingProtection)
  const WFDQuote = await withWorryFreeQuote(initialCart)
  if (!WFDQuote.warrantyOfferId) {
    log('No WFD Quote', { initialCart, WFDQuote, shippingProtections })

    if (shippingProtections.length > 0) {
      await removeWFD(initialCart)
      const refreshedCart = await getCart()
      return refreshedCart
    }

    return initialCart
  }
  if (!isFeatureEnabled('worryFreeDelivery')) {
    return initialCart
  }
  const cartWithWorryFreeQuote = {
    ...initialCart,
    warrantyOfferId: WFDQuote?.warrantyOfferId,
    warrantyOfferPrice: WFDQuote?.warrantyOfferPrice,
    warrantyOfferItems: WFDQuote?.warrantyOfferItems,
  } as StandardCart
  log('cartWithWorryFreeQuote', cartWithWorryFreeQuote)
  const customerOptedOut = customerOptedOutOfWFD()
  const applyByDefault = (
    !initialCart.warrantyOfferId?.length
    && !!cartWithWorryFreeQuote.warrantyOfferId?.length
    && !customerOptedOut
  )
  const quoteChanged = (
    initialCart.warrantyOfferPrice
    && WFDQuote
    && initialCart.warrantyOfferPrice !== cartWithWorryFreeQuote.warrantyOfferPrice
  )
  const itemsChanged = (
    cartWithWorryFreeQuote
    && !initialCart.warrantyOfferId?.length && shippingProtections.length > 0
  )
  const shouldApplyNewWFDQuote = applyByDefault || itemsChanged || quoteChanged
  log('shouldApplyNewWFDQuote', {
    shouldApplyNewWFDQuote,
    applyByDefault,
    quoteChanged,
    itemsChanged,
  })
  if (shouldApplyNewWFDQuote) {
    await removeWFD(initialCart)
    if (cartWithWorryFreeQuote.warrantyOfferId) {
      await rawAddWarranty(cartWithWorryFreeQuote)
    }
    const newCart = await getCart()
    return newCart
  }
  debug('getCart END', { cart: cartWithWorryFreeQuote })
  return cartWithWorryFreeQuote
}

export default getCart
